import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// ONE-9883: Product code identifier for UV DTF products.
const PRODUCT_CODE_UV_DTF = '_w80_';

const misc = `
  webproduct_id
  code
  selection_sku
  dpo_quote
  base_sku
  product_name
  product_type
  product_category
  delivery_time {
    express
    days
    text
  }
`;

const config = `
config {
  overwrite_icons
  show_roll_label_options
  is_custom_shape
  custom_quantity {
    is_enabled
    min
    max
    step
  }
  custom_format {
    is_enabled
    width {
      min
      max
    }
    height {
      min
      max
    }
  }
}`;

const currentOptions = `
current_options {
  id
  value
  is_product_part
  is_out_of_stock
}`;

const pageOptions = `
page_options(pageOptionsHide: $pageOptionsHide, pageOptionsFilter: $pageOptionsFilter) {
  id
  label
  selection_id
  selection_label
  selection_sub_label
  selection_custom_size_type
  is_hidden
  default_value
  tooltip
  values {
    id
    label
    sub_label
    image
    is_selected
    tooltip
    is_out_of_stock
    additional_information
  }
}`;

const prices = `
prices {
  id
  label
  labels {
    singular_price_label
    singular_product_label
    plural_price_label
    plural_product_label
  }
  selection_discount_percentage
  selection_quantity
  selection_number_of_single_products
  selection_price {
    gross {
      price
      price_total
      formatted_price
      formatted_price_total
      formatted_special_price
      formatted_special_price_total
      info_text
    }
    net {
      formatted_price
      formatted_price_total
      formatted_special_price
      formatted_special_price_total
      info_text
    }
  }
  values {
    id
    is_selected
    quantity
    number_of_single_products
    discount_percentage
    price {
      gross {
        formatted_price
        formatted_price_total
        formatted_special_price
        formatted_special_price_total
        info_text
      }
      net {
        formatted_price
        formatted_price_total
        formatted_special_price
        formatted_special_price_total
        info_text
      }
    }
  }
}`;

const PAGEOPTION_QUERY = `query($productId: ID!, $client: String, $customVat: Float, $defaultConfig: String, $optionsOverwrite: OptionsInput, $customQuantities: [Int], $pageOptionsHide: [String], $pageOptionsFilter: [String], $priceListStyle: String, $denyCustomFormats: Boolean) {
 webProduct(
    productId: $productId
    client: $client
    customVat: $customVat,
    defaultConfig: $defaultConfig
    optionsOverwrite: [ $optionsOverwrite ]
    customQuantities: $customQuantities
    priceListStyle: $priceListStyle
    denyCustomFormats: $denyCustomFormats
  ) {
    ${misc}
    ${config}
    ${pageOptions}
    ${currentOptions}
    ${prices}
  }
}`;

const PRICE_QUERY = `query($productId: ID!, $defaultConfig: String, $optionsOverwrite: OptionsInput, $customQuantities: [Int], $priceListStyle: String, $denyCustomFormats: Boolean, $client: String, $customVat: Float) {
 webProduct(
    productId: $productId
    client: $client
    customVat: $customVat,
    defaultConfig: $defaultConfig
    optionsOverwrite: [ $optionsOverwrite ]
    customQuantities: $customQuantities
    priceListStyle: $priceListStyle
    denyCustomFormats: $denyCustomFormats
  ) {
    ${misc}
    ${currentOptions}
    ${prices}
  }
}`;

export const PAGE_OPTION_VALUE_QUERY = `query($productId: ID!, $defaultConfig: String, $optionsOverwrite: OptionsInput, $client: String) {
 webProduct(
    productId: $productId
    client: $client
    defaultConfig: $defaultConfig
    optionsOverwrite: [ $optionsOverwrite ]
  ) {
   page_options {
      id
      values {
        id
      }
    }
  }
}`;

export const sendQuery = async (
  { baseUrl, headers, query, variables, isInitialRequest },
  rejectWithValue,
) => {
  try {
    const response = await fetch(baseUrl + '/graphql', {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...headers,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, variables }),
    });

    let result = await response.json();
    if (response.status === 200) {
      return { data: result.data, variables, isInitialRequest };
    } else {
      if (rejectWithValue) {
        if (response.status === 503) {
          return rejectWithValue(
            Drupal.t(
              'We are currently performing maintenance work. Please try again in a few minutes.',
            ),
          );
        }
        return rejectWithValue(
          Drupal.t(
            'Cannot connect to server. Please try again in a few minutes.',
          ),
        );
      }
    }
  } catch (e) {
    if (rejectWithValue) {
      return rejectWithValue(
        Drupal.t('Cannot connect to server. Please try again in a few minutes.'),
      );
    } else {
      console.error(e.message);
    }
  }
};

/**
 * Applies attribute image rules to a media array.
 * Used for pre-applying rules during instance switch to avoid flash.
 *
 * @param {Array} media - The original media array.
 * @param {Array} rules - The attribute_image_rules array from the shared slice.
 * @param {string} baseSku - The base product code to filter rules by.
 * @param {Array} currentOptions - Array of {id, value} current option selections.
 * @returns {Array} The modified media array, or the original if no rule matches.
 */
export const applyAttributeImageRules = (media, rules, baseSku, currentOptions = []) => {
  if (!rules || !rules.length || !baseSku) return media;

  const applicableRules = rules.filter((rule) => rule.base_product === baseSku);
  if (!applicableRules.length) return media;

  const currentOptionsMap = {};
  for (const opt of currentOptions) {
    currentOptionsMap[opt.id] = opt.value;
  }

  let bestRule = null;
  let bestSpecificity = 0;

  for (const rule of applicableRules) {
    const conditions = rule.conditions || {};
    const conditionKeys = Object.keys(conditions);
    if (conditionKeys.length === 0) continue;

    const allMatch = conditionKeys.every(
      (key) => currentOptionsMap[key] === conditions[key],
    );

    if (allMatch && conditionKeys.length > bestSpecificity) {
      bestSpecificity = conditionKeys.length;
      bestRule = rule;
    }
  }

  if (bestRule && bestRule.images && bestRule.images.length > 0) {
    const remaining = media.slice(bestRule.images.length);
    return [...bestRule.images, ...remaining];
  }

  return media;
};

export const fetchChangeOptions = createAsyncThunk(
  'matrix/fetchChangeOptions',
  async ({ variables, isInitialRequest = false, instanceMedia = null }, { getState, rejectWithValue }) => {
    const {
      baseUrl,
      headers = {},
      pageOptionsHide = [],
      pageOptionsFilter = [],
      priceListStyle = '',
      denyCustomFormats = true,
      optionsOverwrite = [],
    } = getState().matrix;

    variables = {
      pageOptionsHide,
      pageOptionsFilter,
      priceListStyle,
      denyCustomFormats,
      optionsOverwrite,
      ...variables,
    };

    // Pass attribute_image_rules from the shared slice (page-level, survives
    // instance switches) so handleOptionsResponse can access them.
    const attributeImageRules = getState().shared?.attribute_image_rules || [];

    try {
      const response = await sendQuery(
        {
          baseUrl,
          headers,
          query: PAGEOPTION_QUERY,
          variables,
          isInitialRequest,
        },
        rejectWithValue,
      );
      response.attributeImageRules = attributeImageRules;
      response.instanceMedia = instanceMedia;
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const setQuantity = createAsyncThunk(
  'matrix/changeQuantity',
  async ({ variables }, { getState, rejectWithValue }) => {
    const {
      baseUrl,
      headers = {},
      priceListStyle = '',
      denyCustomFormats,
    } = getState().matrix;
    variables = { ...variables, priceListStyle, denyCustomFormats };
    return sendQuery(
      {
        baseUrl,
        headers,
        query: PRICE_QUERY,
        variables,
      },
      rejectWithValue,
    );
  },
);

const handleOptionsResponse = (state, payload) => {
  state.isFetching = false;
  state.isError = false;
  state.isSuccess = true;

  const {
    current_options,
    selection_sku,
    base_sku,
    product_type,
    product_category,
    product_name,
    config,
    page_options,
    prices,
    dpo_quote,
    delivery_time: { days, text, express },
    webproduct_id,
    code,
    AveryCustom,
  } =
    payload.data.webProduct;

  const { isInitialRequest } = payload;
  if (state.optionsOverwrite === null) {
    state.optionsOverwrite = {};
  }
  for (const currentOption of current_options) {
    state.lastChanges[currentOption.id] = state.optionsOverwrite[currentOption.id] !== currentOption.value && isInitialRequest === false;
    state.optionsOverwrite[currentOption.id] = currentOption.value;
  }

  if (state.media?.[0] && state.media?.[0].dynamic === true) {
    const parts = [
      state.optionsOverwrite['shape'],
      state.optionsOverwrite['format'],
      state.optionsOverwrite['paper'],
    ];
    const imageEndpoint = '/api/product-image/{style}/' + parts.join('/');

    state.media[0]['image'] = imageEndpoint.replace(
      '{style}',
      'crop_1_1_ratio_style',
    );
    state.media[0]['thumbnail'] = imageEndpoint.replace(
      '{style}',
      'crop_1_1_ratio_style',
    );
    state.media[0]['placeholder'] = imageEndpoint.replace(
      '{style}',
      'crop_1_1_ratio_style_placeholder',
    );
  }

  // On instance switch, set the new instance's media before applying rules.
  if (payload.instanceMedia && isInitialRequest) {
    state.media = payload.instanceMedia;
    state.original_media = null;
  }

  // Attribute marketing image swap (Custom Printing) — specificity-based matching.
  // Only enter this block when at least one rule applies to the current product.
  // Otherwise the revert branch below would overwrite media set by other mechanisms
  // (e.g. the dynamic blank-label endpoint above) with a stale snapshot.
  const attributeImageRules = payload.attributeImageRules || [];
  const currentBaseSku = code;
  const hasApplicableRules =
    !!currentBaseSku &&
    attributeImageRules.some((rule) => rule.base_product === currentBaseSku);

  if (hasApplicableRules) {
    // On initial request (page load or instance switch), reset original_media.
    if (isInitialRequest || !state.original_media) {
      state.original_media = JSON.parse(JSON.stringify(state.media));
    }

    const swappedMedia = applyAttributeImageRules(
      state.original_media, attributeImageRules, currentBaseSku, current_options,
    );

    if (swappedMedia !== state.original_media) {
      // A rule matched — use the swapped media.
      state.media = swappedMedia;
    } else if (state.lastChanges && Object.values(state.lastChanges).some(Boolean)) {
      // An option changed but no rule matched — revert to original gallery.
      state.media = JSON.parse(JSON.stringify(state.original_media));
    }
  }

  if (config['overwrite_icons'] && config['overwrite_icons'].length > 0) {
    const iconsObject = {};
    config['overwrite_icons'].forEach((ele, i) => {
      const sep = ele.split(':');
      if (sep.length === 2) {
        iconsObject[sep[0]] = sep[1];
      }
    });
    config['overwrite_icons'] = iconsObject;
  }

  state.startTracking = isInitialRequest === false;
  state.config = config;
  state.selectionSku = selection_sku;
  state.baseSku = base_sku;
  state.webproductId = webproduct_id;
  state.code = code;
  state.productName = product_name;
  state.productType = product_type;
  state.productCategory = product_category;
  state.currentOptions = current_options;
  state.pageOptions = page_options;
  state.prices = prices;
  state.dpoQuote = JSON.parse(dpo_quote);
  state.deliveryTime.days = days;
  state.deliveryTime.text = text;
  state.deliveryTime.express = express;
  state.fetched = true;
  state.lastUpdate = new Date().toISOString();

  const { variables: { defaultConfig, productId, priceListStyle, denyCustomFormats, pageOptionsFilter, pageOptionsHide, }} = payload;
  state.priceListStyle = priceListStyle;
  state.productId = productId;
  state.defaultConfig = defaultConfig;
  state.denyCustomFormats = denyCustomFormats;
  state.pageOptionsFilter = pageOptionsFilter;
  state.pageOptionsHide = pageOptionsHide;
  if (!config.is_custom_shape) {
    state.customShape.dpoData = null;
  }
};

export const initialMatrixState = {
  baseUrl: '',
  isError: false,
  isFetching: false,
  isSuccess: false,
  fetched: false,
  startTracking: false,
  baseSku: null,
  productName: null,
  productType: null,
  productCategory: null,
  customShape: {
    dpoData: null,
    addToCartFormData: {},
  },
  errorMessage: '',
  errors: [],
  productId: null,
  code: null,
  selectionSku: null,
  webproductId: null,
  defaultConfig: '',
  headers: {},
  pageOptions: [],
  pageOptionsHide: [],
  pageOptionsFilter: [],
  currentOptions: [],
  customQuantities: [],
  optionsOverwrite: {},
  config: {},
  media: [],
  original_media: null,
  previewIconsPath: '',
  denyCustomFormats: false,
  productTags: '',
  dpoQuote: '',
  deliveryTime: {
    days: 0,
    text: '',
    express: true,
  },
  lastChanges: {},
  lastUpdate: new Date().toISOString(),
  AveryCustom: {
    client: '',
    shopUrl: '',
    assetUrl: '',
  },
};

export const matrixSlice = createSlice({
  name: 'matrix',
  initialState: initialMatrixState,
  reducers: {
    setPreloadedOptions: (state, { payload }) => {
      handleOptionsResponse(state, payload);
    },
    overwriteState: (state, { payload }) => {
      return { ...state, ...payload, ...{ customShape: { dpoData: null } } };
    },
    storeDpoData: (state, { payload }) => {
      state.customShape.dpoData = payload;
    },
    resetDpoData: (state) => {
      state.customShape.dpoData = null;
    },
    storeAddToCartFormData: (state, { payload }) => {
      state.customShape.addToCartFormData = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchChangeOptions.pending, (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    });

    builder.addCase(fetchChangeOptions.fulfilled, (state, { payload }) => {
      handleOptionsResponse(state, payload);
    });

    builder.addCase(fetchChangeOptions.rejected, (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;

      state.errorMessage = payload;
    });

    builder.addCase(setQuantity.pending, (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    });

    builder.addCase(setQuantity.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      state.isError = false;
      state.isSuccess = true;
      const {
        variables: { customQuantities = [] },
      } = payload;
      const {
        current_options,
        prices,
        dpo_quote,
        delivery_time: { days, text, express },
      } = payload.data.webProduct;

      const { selection_quantity } = prices;
      state.optionsOverwrite['quantity'] = `quantity_${selection_quantity}`;
      for (const currentOption of current_options) {
        state.lastChanges[currentOption.id] = state.optionsOverwrite[currentOption.id] !== currentOption.value;
        state.optionsOverwrite[currentOption.id] = currentOption.value;
      }
      state.startTracking = true;
      state.currentOptions = current_options;
      state.prices = prices;
      state.customQuantities = customQuantities;
      state.dpoQuote = JSON.parse(dpo_quote);
      state.deliveryTime.days = days;
      state.deliveryTime.text = text;
      state.deliveryTime.express = express;
      state.lastUpdate = new Date().toISOString();
    });

    builder.addCase(setQuantity.rejected, (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;

      state.errorMessage = payload;
    });
  },
});

export const {
  overwriteState,
  storeDpoData,
  storeAddToCartFormData,
  resetDpoData,
  setPreloadedOptions,
} = matrixSlice.actions;

export const matrixSelector = (state) => state.matrix;
export const mediaSelector = (state) => state.matrix.media;
export const pageOptionByIdSelector = (selectionId) => (state) => {
  return state.matrix.pageOptions
    .filter(({ id }) => id === selectionId)
    .shift();
};
export const pageOptionsSelector = (onlyVisible = true, type = 'default') => (state) => {
  let pageOptions = state.matrix.pageOptions;
  const pamRoll = new RegExp('pam.*?roll_labels');
  if (onlyVisible) {
    pageOptions = pageOptions.filter(({ is_hidden }) => is_hidden === false);
  }

  const isUvDtf = state.matrix.code && state.matrix.code.toLowerCase().includes(PRODUCT_CODE_UV_DTF);

  if (type === 'default') {
    if (state.matrix.config.is_custom_shape) {
      if (state.matrix.customShape.dpoData) {
        return pageOptions.filter(({ id }) => id !== 'model' && id !== 'refinement');
      } else {
        // US-Approach with filters below
        // pageOptions = pageOptions.filter(({id}) => id !== 'paper' && id !== 'finishing');
        return pageOptions.filter(({ id }) => id !== 'model' && id !== 'refinement');
      }
    } else if (pamRoll.test(state.matrix.code) || isUvDtf) {
      // Roll labels and UV DTF products use model and refinement as page options.
      return pageOptions;
    } else {
      pageOptions = pageOptions.filter(({ id }) => id !== 'model' && id !== 'refinement');
    }
  } else if (type === 'roll') {
    pageOptions = pageOptions.filter(({ id }) => id === 'model' || id === 'refinement');
  }

  return pageOptions;
};

export const dataLayerPayloadSelector = (state) => {
  const {
    baseSku: item_id,
    productName: item_name,
    productType: item_brand,
    productCategory: item_category,
    selectionSku: item_variant,
    pageOptions,
    prices: { selection_number_of_single_products, selection_quantity: quantity, selection_price: { gross: { price, price_total: value } } },
  } = state.matrix;

  const { currencySymbol: currency } = state.app;

  const categories = {
    item_category2: 'format',
    item_category3: 'shape',
    item_category4: 'paper',
    item_category5: 'finishing',
  };

  for (const key in categories) {
    const { selection_label } = pageOptions.find(({ id }) => id === categories[key]);
    if (selection_label) {
      categories[key] = selection_label;
    } else {
      delete categories[key];
    }
  }

  function isValidNumber(n) {
    return !isNaN(n) && n > 1;
  }

  if (isValidNumber(quantity) && isValidNumber(selection_number_of_single_products)) {
    categories['item_category6'] = selection_number_of_single_products / quantity;
  }

  return {
    currency,
    value,
    item_id,
    item_name,
    item_brand,
    item_category,
    item_variant,
    price: parseFloat(price.toFixed(4)),
    quantity,
    ...categories,
  };
};

export default matrixSlice.reducer;
